import React, { useContext, useMemo } from "react";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import useWhiteLabelConfig from "@hooks/useWhiteLabelConfig";
import { AuthContext } from "@context/AuthContext";
import { BookSessionContext } from "@context/BookSessionContext";
import Loader from "@components/Ui/Loader";
import Drawer from "@components/V3/Drawer";
import "./style.scss";

// loadStripe must only be called once per key, so cache the promise across mounts.
const stripeCache = new Map();
const getStripePromise = (publicKey) => {
    if (!publicKey) return null;
    if (!stripeCache.has(publicKey)) {
        stripeCache.set(publicKey, loadStripe(publicKey));
    }
    return stripeCache.get(publicKey);
};

// Checkout drawer for groups switched to Stripe in Admin (white label payment_gateway).
// Mounts Stripe Embedded Checkout from a session client_secret created by
// /ticket/initialise-stripe. Only opens once that secret exists.
const StripeCheckoutDrawer = () => {
    const { isStripeEnabled, stripePublicKey } = useWhiteLabelConfig();
    const {
        drawerState,
        closeDrawer,
        stripeClientSecret,
        setStripeClientSecret,
        setStripeSessionId,
    } = useContext(AuthContext);
    const { setProceedToCheckout, pollPaymentStatus, uuid } = useContext(BookSessionContext);

    const stripePromise = useMemo(
        () => getStripePromise(stripePublicKey),
        [stripePublicKey]
    );


    const isOpen = !!drawerState.stripeCheckoutDrawer;

    const handleClose = () => {
        const hadSession = !!stripeClientSecret;
        closeDrawer("stripeCheckoutDrawer");
        // Drop the client secret so a stale session can't be reopened. A fresh
        // /initialise-stripe call is required if the user comes back.
        setStripeClientSecret(null);
        setStripeSessionId(null);

        // Closed while still loading: no session was shown, so there is nothing to poll.
        if (!hadSession) {
            setProceedToCheckout(false);
            return;
        }

        pollPaymentStatus(uuid).then((succeeded) => {
            if (!succeeded) {
                setProceedToCheckout(false);
            }
        });
    };


    if (!isStripeEnabled || !stripePromise) {
        return null;
    }

    return (
        <Drawer
            id="stripeCheckoutDrawer"
            role="dialog"
            aria-modal="true"
            aria-label="Payment checkout"
            isOpen={isOpen}
            onClose={handleClose}
            closeButtonAlignment="right"
        >
            <div className="stripe-checkout-drawer">
                {/* Keyed on the secret so React remounts on change; the provider
                    cannot be updated in place. */}
                <Loader isLoading={isOpen && !stripeClientSecret} text="Preparing secure checkout…" />
                {isOpen && stripeClientSecret && (
                    <EmbeddedCheckoutProvider
                        key={stripeClientSecret}
                        stripe={stripePromise}
                        options={{ clientSecret: stripeClientSecret }}
                    >
                        <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                )}
            </div>
        </Drawer>
    );
};

export default StripeCheckoutDrawer;
